import React from 'react';
import { connect } from 'react-redux';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal } from './Overlay';
import { Fieldset, SettingRow, Switch, Field } from './Form';

/*
 * SMS providers — layar `sms` paket client.
 *
 * Empat kolom data prototipe tidak punya penyimpanan: Sent (30d), Failed, Cost,
 * dan status tiga tingkat Active/Standby/Inactive. SmsMethods menyimpan sebuah
 * boolean, kredensial, dan nomor pengirim — tidak ada catatan pengiriman dan
 * tidak ada harga di mana pun. Menampilkan kolom "Failed" yang selalu nol lebih
 * buruk daripada tidak menampilkannya.
 *
 * Yang dipakai menggantikannya justru yang menentukan: apakah kredensialnya
 * lengkap, dan mana yang sedang dipakai mengirim OTP.
 *
 * KENAPA LAYAR INI BERBAHAYA. OTP adalah satu-satunya cara masuk ke kedua
 * aplikasi. Pengirimnya membutuhkan tepat satu baris berstatus aktif; dengan
 * NOL gateway aktif, tidak ada
 * satu pun OTP terkirim — tidak ada yang bisa mendaftar atau login sampai
 * seseorang menyadarinya. Karena itu mematikan gateway terakhir ditolak di
 * sini, bukan sekadar diperingatkan.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `{
  getAdminSmsMethods {
    activeCount status errorMessage
    results { id name isActive accountId hasSecret secretHint senderNumber updatedAt incomplete }
  }
}`;

const M_SAVE = `mutation ($id: Int, $status: String, $accountId: String, $securityId: String,
                 $phoneNumber: String, $phoneDialCode: String, $updateStatus: Boolean) {
  updateSmsMethod(id: $id, status: $status, accountId: $accountId, securityId: $securityId,
                  phoneNumber: $phoneNumber, phoneDialCode: $phoneDialCode,
                  updateStatus: $updateStatus) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;

const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
};

class SmsProviders extends React.Component {
  state = {
    rows: [], activeCount: 0, loading: true, error: null,
    form: null, formError: null, saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  isArabic = () => String(this.props.currentLocale || '').startsWith('ar');

  tx = (en, ar) => this.isArabic() ? ar : en;

  errorText = (message, fallbackEn, fallbackAr) => {
    if (!this.isArabic()) return message || fallbackEn;
    const value = String(message || '').toLowerCase();
    if (value.includes('only active')) return 'لا يمكن إيقاف البوابة النشطة الوحيدة. فعّل بوابة بديلة أولاً.';
    if (value.includes('exactly one')) return 'يجب أن تبقى بوابة رسائل نصية واحدة نشطة فقط.';
    if (value.includes('account id')) return 'تحتاج البوابة النشطة إلى معرّف حساب ورمز سري ورقم مرسل.';
    if (value.includes('not found')) return 'لم يتم العثور على بوابة الرسائل النصية.';
    if (value.includes('length')) return 'تتجاوز بيانات اعتماد البوابة الطول المدعوم.';
    return fallbackAr;
  };

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q).catch(() => null);
    const res = json && json.data && json.data.getAdminSmsMethods;
    if (!ok(res)) {
      this.setState({ loading: false, error: this.errorText(res && res.errorMessage,
        'Could not load the gateways.', 'تعذّر تحميل بوابات الرسائل النصية.') });
      return;
    }
    this.setState({
      rows: res.results || [], activeCount: res.activeCount || 0, loading: false
    });
  };

  openEdit = r => this.setState({
    form: {
      id: r.id, name: r.name, isActive: r.isActive,
      accountId: r.accountId || '',
      // Token tidak pernah dimuat: kosong berarti "biarkan apa adanya".
      securityId: '', hasSecret: r.hasSecret, secretHint: r.secretHint,
      /*
       * SATU KOLOM, BUKAN DUA. Skemanya punya phoneNumber dan phoneDialCode,
       * tapi baris yang ada menyimpan nomor lengkap termasuk '+' di
       * phoneNumber dan membiarkan phoneDialCode kosong — dan itulah yang
       * dibaca pengirim sebagai `from`. Memecahnya jadi dua kotak menulis
       * balik potongannya dan merusak nomornya; saya sudah melakukannya sekali.
       */
      phoneNumber: r.senderNumber || '',
      makeActive: r.isActive
    },
    formError: null, notice: null
  });

  setForm = (key, value) => this.setState(st => ({ form: { ...st.form, [key]: value } }));

  submit = async () => {
    const f = this.state.form;
    const { activeCount } = this.state;

    // Menonaktifkan satu-satunya gateway aktif mematikan login di kedua aplikasi.
    if (f.isActive && !f.makeActive && activeCount <= 1) {
      this.setState({
        formError: this.tx(
          'This is the only active gateway. Switching it off stops every OTP, so nobody could sign in. Activate the other gateway instead.',
          'هذه هي البوابة النشطة الوحيدة. سيؤدي إيقافها إلى توقف جميع رموز التحقق ومنع تسجيل الدخول. فعّل البوابة الأخرى بدلاً منها.'
        )
      });
      return;
    }
    if (f.makeActive && (!f.accountId.trim() || (!f.hasSecret && !f.securityId.trim())
      || !f.phoneNumber.trim())) {
      this.setState({
        formError: this.tx('An active gateway needs an account id, a secret and a sender number.',
          'تحتاج البوابة النشطة إلى معرّف حساب ورمز سري ورقم مرسل.')
      });
      return;
    }

    this.setState({ saving: true, formError: null });

    const vars = {
      id: f.id,
      status: f.makeActive ? '1' : '0',
      accountId: f.accountId.trim(),
      phoneNumber: f.phoneNumber.trim(),
      // Tidak disentuh: kolom ini kosong pada baris yang ada, dan pengirimnya
      // tidak membacanya sama sekali.
      phoneDialCode: undefined,
      // Hanya dikirim kalau diketik ulang; kosong berarti biarkan yang tersimpan.
      securityId: f.securityId.trim() || undefined,
      // Membuat gateway ini aktif akan mematikan yang lain, di sisi server.
      updateStatus: !!f.makeActive
    };

    const json = await gql(M_SAVE, vars).catch(() => null);
    const res = json && json.data && json.data.updateSmsMethod;
    this.setState({ saving: false });

    if (!ok(res)) {
      this.setState({ formError: this.errorText(res && res.errorMessage,
        'Could not save the gateway.', 'تعذّر حفظ بوابة الرسائل النصية.') });
      return;
    }
    this.setState({
      form: null,
      notice: {
        tone: 'info',
        text: f.makeActive
          ? this.tx(`${f.name} is now the gateway every OTP is sent through.`,
            `أصبحت ${f.name} الآن البوابة المستخدمة لإرسال جميع رموز التحقق.`)
          : this.tx(`${f.name} saved.`, `تم حفظ ${f.name}.`)
      }
    });
    this.load(true);
  };

  columns() {
    return [
      {
        key: 'name', label: this.tx('Gateway', 'البوابة'), sortable: true,
        render: r => (
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            <b>{r.name}</b>
            {r.isActive && <span className="tag amber"><i />{this.tx('Sending OTPs', 'ترسل رموز التحقق')}</span>}
          </span>
        )
      },
      {
        key: 'accountId', label: this.tx('Account', 'الحساب'),
        render: r => r.accountId
          ? <span className="mono" title={r.accountId}>{r.accountId.slice(0, 12)}…</span>
          : <span className="tag danger"><i />{this.tx('Not set', 'غير معيّن')}</span>
      },
      {
        // Nilainya tidak pernah meninggalkan server; yang ditampilkan hanya
        // bahwa ia terisi, plus empat huruf terakhir untuk mencocokkan.
        key: 'hasSecret', label: this.tx('Secret', 'الرمز السري'),
        render: r => r.hasSecret
          ? <span className="mono">{r.secretHint}</span>
          : <span className="tag danger"><i />{this.tx('Not set', 'غير معيّن')}</span>
      },
      {
        key: 'senderNumber', label: this.tx('Sends from', 'رقم الإرسال'),
        render: r => r.senderNumber
          ? <span className="mono">{r.senderNumber}</span>
          : <span className="tag danger"><i />{this.tx('Not set', 'غير معيّن')}</span>
      },
      {
        key: 'isActive', label: this.tx('Status', 'الحالة'),
        render: r => r.isActive
          ? <span className="tag ok"><i />{this.tx('Active', 'نشطة')}</span>
          : (
            <span className={`tag ${r.incomplete ? 'danger' : ''}`}>
              <i />{r.incomplete ? this.tx('Not configured', 'غير مهيأة') : this.tx('Standby', 'احتياطية')}
            </span>
          )
      },
      {
        key: 'updatedAt', label: this.tx('Updated', 'آخر تحديث'), align: 'end', sortable: true,
        render: r => <span className="mono">{day(r.updatedAt) || '—'}</span>
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <button className="btn icon" type="button"
            title={this.tx(`Configure ${r.name}`, `تهيئة ${r.name}`)}
            aria-label={this.tx(`Configure ${r.name}`, `تهيئة ${r.name}`)} onClick={() => this.openEdit(r)}>
            <Icon name="edit" />
          </button>
        )
      }
    ];
  }

  renderForm() {
    const f = this.state.form;
    const { saving, formError, activeCount } = this.state;
    const lastActive = f.isActive && activeCount <= 1;

    return (
      <Modal
        icon="sms" wide
        title={this.tx(`Configure ${f.name}`, `تهيئة ${f.name}`)}
        sub={this.tx("Credentials come from the provider's own console.", 'تأتي بيانات الاعتماد من لوحة تحكم مزوّد الخدمة.')}
        confirmLabel={this.tx('Save gateway', 'حفظ البوابة')}
        cancelLabel={this.tx('Cancel', 'إلغاء')}
        workingLabel={this.tx('Saving…', 'جارٍ الحفظ…')}
        busy={saving}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

        <Fieldset title={this.tx('Credentials', 'بيانات الاعتماد')}>
          <Field label={this.tx('Account id', 'معرّف الحساب')}
            hint={f.name === 'Twilio'
              ? this.tx('The Account SID from the Twilio console.', 'معرّف الحساب SID من لوحة Twilio.')
              : this.tx('The API key.', 'مفتاح واجهة API.')}>
            <input className="inp mono" value={f.accountId}
              onChange={e => this.setForm('accountId', e.target.value)} />
          </Field>
          <Field label={this.tx('Secret', 'الرمز السري')}
            hint={f.hasSecret
              ? this.tx(`A secret ending ${f.secretHint} is stored. Leave blank to keep it.`,
                `تم حفظ رمز سري ينتهي بـ ${f.secretHint}. اترك الحقل فارغاً للاحتفاظ به.`)
              : this.tx('No secret is stored yet.', 'لم يتم حفظ رمز سري بعد.')}>
            {/* Nilainya tidak pernah dikirim ke peramban, jadi kotak ini selalu
                mulai kosong: mengisinya berarti mengganti, membiarkannya
                berarti mempertahankan. */}
            <input className="inp mono" type="password" autoComplete="new-password"
              value={f.securityId} placeholder={f.hasSecret
                ? this.tx('Unchanged', 'بدون تغيير')
                : this.tx('Paste the secret', 'ألصق الرمز السري')}
              onChange={e => this.setForm('securityId', e.target.value)} />
          </Field>
          <Field label={this.tx('Sends from', 'رقم الإرسال')}
            hint={this.tx('The full number, with country code, that the passcode arrives from.',
              'الرقم الكامل مع رمز الدولة الذي يصل منه رمز التحقق.')}>
            <input className="inp mono" value={f.phoneNumber} placeholder="+17433006296"
              onChange={e => this.setForm('phoneNumber', e.target.value)} />
          </Field>
        </Fieldset>

        <Fieldset title={this.tx('Use for passcodes', 'الاستخدام لرموز التحقق')} rows>
          <SettingRow
            label={this.tx('Send OTPs through this gateway', 'إرسال رموز التحقق عبر هذه البوابة')}
            hint={this.tx('Exactly one gateway sends passcodes. Turning this on turns the other one off.',
              'ترسل بوابة واحدة فقط رموز التحقق. سيؤدي تفعيلها إلى إيقاف البوابة الأخرى.')}
            note={lastActive && !f.makeActive
              ? this.tx('Locked: this is the only active gateway. Activate the other one instead.',
                'مقفل: هذه هي البوابة النشطة الوحيدة. فعّل البوابة الأخرى بدلاً منها.')
              : null}>
            <Switch checked={f.makeActive}
              label={this.tx('Send OTPs through this gateway', 'إرسال رموز التحقق عبر هذه البوابة')}
              disabled={lastActive}
              onChange={v => this.setForm('makeActive', v)} />
          </SettingRow>
        </Fieldset>
      </Modal>
    );
  }

  render() {
    const { rows, activeCount, loading, error, form, notice } = this.state;
    const active = rows.find(r => r.isActive);

    return (
      <AdminShell
        current="sms"
        breadcrumb={[this.tx('Communications', 'الاتصالات'), this.tx('SMS providers', 'مزودو الرسائل النصية')]}
        title={this.tx('SMS providers', 'مزودو الرسائل النصية')}
        lead={active
          ? this.tx(`Passcodes are sent through ${active.name}.`, `تُرسل رموز التحقق عبر ${active.name}.`)
          : this.tx('Gateways used for one-time passcodes.', 'البوابات المستخدمة لإرسال رموز التحقق لمرة واحدة.')}
        actions={[
          <a className="btn ghost" href="/siteadmin/sms-methods" key="c">
            {this.tx('Open in classic', 'فتح النسخة الكلاسيكية')}
          </a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>{this.tx('Dismiss', 'إغلاق')}</button>
          </div>
        )}

        <div className="notice danger">
          <span className="ni">!</span>
          <span className="nt">
            {this.tx(
              'A one-time passcode is the only way into either app. If no gateway is active, no passcode is sent and nobody can sign in or register — existing sessions keep working, so it can go unnoticed for a while.',
              'رمز التحقق لمرة واحدة هو وسيلة الدخول الوحيدة إلى التطبيقين. إذا لم تكن هناك بوابة نشطة، فلن يُرسل أي رمز ولن يتمكن أحد من تسجيل الدخول أو إنشاء حساب، بينما تستمر الجلسات الحالية بالعمل وقد لا تُلاحظ المشكلة فوراً.'
            )}
          </span>
        </div>

        {/* Dua gateway aktif berarti yang terpakai ditentukan urutan baris yang
            kebetulan dikembalikan MySQL. */}
        {!loading && activeCount > 1 && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {this.tx(
                `${activeCount} gateways are active at once. The sender refuses this invalid configuration until exactly one gateway remains active.`,
                `${activeCount} بوابات نشطة في الوقت نفسه. سيرفض نظام الإرسال هذا الإعداد غير الصالح حتى تبقى بوابة نشطة واحدة فقط.`
              )}
            </span>
          </div>
        )}

        {!loading && activeCount === 0 && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {this.tx(
                'No gateway is active. Passcodes are not being sent at all — sign-in and registration are broken in both apps right now.',
                'لا توجد بوابة نشطة. لا يتم إرسال رموز التحقق حالياً، ولذلك يتعطل تسجيل الدخول وإنشاء الحساب في التطبيقين.'
              )}
            </span>
          </div>
        )}

        {form && this.renderForm()}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={rows.length}
              page={1}
              perPage={25}
              loading={loading}
              onPage={() => {}}
              onSearch={() => {}}
              searchPlaceholder={this.tx('Search gateway', 'البحث عن بوابة')}
              emptyText={this.tx('No SMS gateways', 'لا توجد بوابات رسائل نصية')}
              emptyHint={this.tx('Without one, passcodes cannot be delivered.', 'لا يمكن إرسال رموز التحقق بدون بوابة.')}
            />
          )}

        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--tx-3)', marginTop: 'var(--sp-3)' }}>
          {/* Bukan detail sepele: baris kedua diperlakukan sebagai Vonage oleh
              pengirim, apa pun nama yang tertulis di situ. */}
          {this.tx(
            'The sender chooses its integration by row id — row 2 is treated as Vonage, anything else as Twilio — so the two rows are not interchangeable. Secrets are never sent to this page; only the last four characters are shown.',
            'يختار نظام الإرسال التكامل حسب معرّف الصف: يُعامل الصف 2 على أنه Vonage وأي صف آخر على أنه Twilio، لذلك لا يمكن تبديل الصفين. لا تُرسل الرموز السرية إلى هذه الصفحة أبداً؛ تظهر آخر أربعة أحرف فقط.'
          )}
        </p>
      </AdminShell>
    );
  }
}

const mapState = state => ({ currentLocale: state && state.intl && state.intl.locale });

export default connect(mapState)(SmsProviders);
